"use client"

import { useEffect, useRef, useState } from "react"
import { cn } from "@/shared/lib/utils"
import { Input } from "@/shared/components/ui/input"
import { Button } from "@/shared/components/ui/button"
import { Icon } from "@/shared/components/icons"
import { useUIStore } from "@/shared/stores/ui-store"
import { track } from "@/shared/lib/analytics"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog"

interface PremiumCustomOptionProps {
  isAuthenticated: boolean
  isPremium: boolean
  isSelected: boolean
  /** The currently saved custom value from the store. Pass "" when no custom value is set. */
  customValue: string
  onCustomChange: (value: string) => void
  placeholder?: string
  dialogTitle?: string
  /** "card" = image-grid card (hair style); "chip" = inline chip (personality) */
  variant?: "card" | "chip"
  /** Max characters allowed (default 80) */
  maxLength?: number
  /** @deprecated No longer needed – kept for API compatibility */
  onSelect?: () => void
  /** Called before opening register/paywall modal so parent dialog can close (avoids double overlay) */
  onBeforeOpenModal?: () => void
}

export function PremiumCustomOption({
  isAuthenticated,
  isPremium,
  isSelected,
  customValue,
  onCustomChange,
  placeholder = "Enter custom value...",
  dialogTitle = "Custom",
  variant = "chip",
  maxLength = 80,
  onSelect: _onSelect,
  onBeforeOpenModal,
}: PremiumCustomOptionProps) {
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const setPaywallContext = useUIStore((s) => s.setPaywallContext)
  const registerModalDismissed = useUIStore((s) => s.registerModalDismissed)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input when dialog opens
  useEffect(() => {
    if (dialogOpen) setTimeout(() => inputRef.current?.focus(), 50)
  }, [dialogOpen])

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isAuthenticated) {
      // The register modal opens at most once per session. After it's been
      // dismissed we must NOT re-pop it on every click (the dead-click loop),
      // but we also must not silently ignore the click — that reads as a broken
      // control. Instead give inline feedback (toast) and still instrument the
      // block so the gate stays visible in the funnel.
      track("character:creation_blocked_unauthenticated", { gate: "custom_option" })
      if (registerModalDismissed) {
        toast.info("Log in to use custom options")
        return
      }
      onBeforeOpenModal?.()
      setActiveModal("register")
      return
    }
    if (!isPremium) {
      onBeforeOpenModal?.()
      setPaywallContext({
        intent: "character_creation",
        trigger: "premium_custom_option",
        characterId: null,
        characterName: null,
        characterAvatar: null,
        messagesSentCount: null,
      })
      setActiveModal("subscribe-paywall")
      return
    }
    setInputValue(customValue)
    setDialogOpen(true)
  }

  const handleApply = () => {
    const value = inputValue.trim()
    if (value) onCustomChange(value)
    setDialogOpen(false)
  }

  const handleClear = () => {
    onCustomChange("")
    setDialogOpen(false)
  }

  const isLocked = !isAuthenticated || !isPremium
  const lockedTitle = !isAuthenticated
    ? "Log in to use custom options"
    : !isPremium
      ? "Upgrade to Pro to use custom options"
      : undefined
  const charCount = inputValue.length
  const isOverLimit = charCount > maxLength

  const dialogNode = (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent className="sm:max-w-sm bg-card border-border/60">
        <DialogHeader className="gap-3">
          <div className="flex items-center gap-3">
            <span className="flex size-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Icon name="flash" size={16} className="size-4" />
            </span>
            <div>
              <DialogTitle className="text-base font-semibold">{dialogTitle}</DialogTitle>
              <DialogDescription className="text-xs mt-0.5">
                Describe exactly what you want
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="relative">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value.slice(0, maxLength + 5))}
              placeholder={placeholder}
              className={cn(
                "bg-background border-border/50 pr-14 h-11 text-sm transition-colors",
                "focus:border-primary/60 focus:ring-1 focus:ring-primary/30",
                isOverLimit && "border-destructive/60 focus:border-destructive/60"
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isOverLimit && inputValue.trim()) handleApply()
                if (e.key === "Escape") setDialogOpen(false)
              }}
            />
            <span className={cn(
              "absolute right-3 top-1/2 -translate-y-1/2 text-xs tabular-nums pointer-events-none",
              isOverLimit ? "text-destructive" : "text-muted-foreground"
            )}>
              {charCount}/{maxLength}
            </span>
          </div>

          {/* Examples tip */}
          <p className="text-[11px] text-muted-foreground/70 leading-relaxed">
            <span className="font-medium text-muted-foreground">Example:</span> {placeholder}
          </p>
        </div>

        <DialogFooter showCloseButton={false} className="gap-2 sm:gap-2">
          {isSelected && customValue && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-muted-foreground hover:text-destructive mr-auto text-xs"
            >
              Clear
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleApply}
            disabled={!inputValue.trim() || isOverLimit}
            className=""
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  // ─── Card variant ─────────────────────────────────────────────────────────
  if (variant === "card") {
    return (
      <>
        <button
          type="button"
          onClick={handleTriggerClick}
          title={lockedTitle}
          aria-label={isSelected ? `Custom: ${customValue}. Click to edit.` : "Add custom value"}
          className={cn(
            "group relative flex flex-col items-center justify-center overflow-hidden rounded-lg border-2",
            "aspect-[3/4] min-h-0 transition-all duration-300 ease-out",
            // selected state
            isSelected && "border-accent-blue/50 ring-1 ring-accent-blue/30",
            // unauth state
            !isAuthenticated && !isSelected && "border-dashed border-border/60 bg-card/40 hover:border-white/30 hover:bg-card/60",
            // auth but not premium
            isAuthenticated && !isPremium && !isSelected && "border-dashed border-amber-500/50 bg-amber-500/5 hover:border-amber-500/70 hover:bg-amber-500/8",
            // premium, not selected
            isPremium && !isSelected && "border-dashed border-primary/40 bg-primary/5 hover:border-primary/70 hover:bg-primary/10",
            // selected
            isSelected && "bg-accent-blue/10"
          )}
        >
          {/* Background shimmer for premium selected */}
          {isSelected && (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
          )}

          <div className="relative z-10 flex flex-col items-center gap-2 p-2 text-center w-full">
            {/* Icon */}
            <div className={cn(
              "flex size-9 items-center justify-center rounded-xl transition-all duration-200",
              isSelected
                ? "bg-primary/20 text-primary"
                : isAuthenticated && isPremium
                  ? "bg-primary/10 text-primary group-hover:bg-primary/20"
                  : isAuthenticated
                    ? "bg-amber-500/15 text-amber-500"
                    : "bg-muted/30 text-muted-foreground"
            )}>
              {isSelected
                ? <Icon name="edit" size={16} className="size-4" />
                : isLocked
                  ? <Icon name="lock" size={16} className="size-4" />
                  : <Icon name="flash" size={16} className="size-4" />
              }
            </div>

            {/* Label */}
            <span className={cn(
              "block font-semibold text-xs leading-tight",
              isSelected ? "text-accent-blue" : isAuthenticated && isPremium ? "text-foreground/90" : "text-muted-foreground"
            )}>
              {isSelected ? "Edit" : "Custom"}
            </span>

            {/* Current value preview */}
            {isSelected && customValue && (
              <span
                className="block text-[10px] text-muted-foreground leading-tight px-1 w-full overflow-hidden line-clamp-2"
                title={customValue}
              >
                {customValue}
              </span>
            )}

            {/* Lock / premium badge for non-premium */}
            {!isLocked ? null : (
              <span className={cn(
                "text-[9px] font-semibold uppercase tracking-wide leading-none px-1.5 py-0.5 rounded-full",
                isAuthenticated ? "bg-amber-500/20 text-amber-500" : "bg-muted/40 text-muted-foreground"
              )}>
                {isAuthenticated ? "Pro" : "Login"}
              </span>
            )}
          </div>

          {/* Check badge when selected */}
          {isSelected && (
            <div className="absolute top-1 right-1 flex size-4 items-center justify-center rounded-full bg-accent-blue">
              <svg className="size-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>

        {dialogNode}
      </>
    )
  }

  // ─── Chip variant ──────────────────────────────────────────────────────────
  return (
    <>
      <button
        type="button"
        onClick={handleTriggerClick}
        title={lockedTitle}
        // The chip stays actionable while locked (it opens the register/paywall
        // flow), so we must NOT announce it as disabled. Instead give assistive
        // tech an accessible name that states the gate action.
        aria-label={lockedTitle}
        className={cn(
          "flex items-center gap-1.5 rounded-xl border-2 px-3 py-2 text-sm font-medium transition-all duration-200 min-h-[40px]",
          isSelected
            ? "border-accent-blue/50 bg-accent-blue/10 text-primary"
            : isAuthenticated && isPremium
              ? "border-dashed border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary/60"
              : isAuthenticated
                ? "border-dashed border-amber-500/40 bg-amber-500/5 text-amber-600 hover:border-amber-500/60"
                : "border-dashed border-border/50 bg-card/30 text-muted-foreground hover:border-white/30"
        )}
      >
        {isSelected
          ? <Icon name="edit" size={12} className="size-3 shrink-0" />
          : isLocked
            ? <Icon name="lock" size={12} className="size-3 shrink-0" />
            : <Icon name="flash" size={12} className="size-3 shrink-0" />
        }
        <span>{isSelected ? "Edit custom" : "Custom"}</span>
        {isSelected && customValue && (
          <span className="truncate max-w-[100px] text-xs opacity-75 ml-0.5" title={customValue}>
            · {customValue}
          </span>
        )}
        {!isLocked ? null : (
          <span className={cn(
            "ml-0.5 text-[10px] font-bold uppercase tracking-wide",
            isAuthenticated ? "text-amber-500" : "text-muted-foreground/60"
          )}>
            {isAuthenticated ? "PRO" : "Login"}
          </span>
        )}
      </button>

      {dialogNode}
    </>
  )
}
